import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import { fmtDate, fmtDateTime } from '../../../utils/ruDates';
import { filterBySearch } from '../../../utils/search';

const STATUS_COLOR = {
  succeeded: '#27ae60', completed: '#27ae60', paid: '#27ae60',
  partial: '#2980b9',
  pending: '#f39c12', issued: '#f39c12', requested: '#f39c12',
  failed: '#e74c3c', canceled: '#e74c3c', rolled_back: '#e74c3c',
};
const sc = s => STATUS_COLOR[s] || '#7f8c8d';

const STATUS_LABEL = {
  issued: 'Выставлен', partial: 'Частично', paid: 'Оплачен',
  cancelled: 'Отменён', succeeded: 'Успешно', pending: 'Ожидание',
  failed: 'Ошибка', canceled: 'Отменена', rolled_back: 'Откат',
  requested: 'Возврат↗',
};
const sl = s => STATUS_LABEL[s] || s;

// Источник денег в поступлениях: карта через ЮKassa, банковский перевод по
// счёту, либо внесено админом руками.
const SOURCE_LABELS = {
  yookassa: 'Карта (ЮKassa)',
  invoice: 'Банк по счёту',
  manual: 'Внесено вручную',
};

const Badge = ({ s }) => (
  <span style={{
    padding: '3px 9px', borderRadius: 12, fontSize: 12, fontWeight: 600,
    background: `${sc(s)}20`, color: sc(s),
  }}>{sl(s)}</span>
);

const Inp = ({ label, ...p }) => (
  <label style={{ display: 'grid', gap: 4 }}>
    <span style={{ fontSize: 12, color: '#666' }}>{label}</span>
    <input style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }} {...p} />
  </label>
);

const Card = ({ children, style }) => (
  <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,.07)', padding: 20, ...style }}>
    {children}
  </div>
);

const TAB_STYLE = active => ({
  padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500,
  background: active ? '#2c3e50' : '#f0f0f0', color: active ? 'white' : '#555',
});

// ─── modals ─────────────────────────────────────────────────────────────────

function PaymentModal({ invoice, accounts, onClose, onSave }) {
  const [form, setForm] = useState({
    amount: invoice ? String(invoice.amount) : '',
    received_at: new Date().toISOString().slice(0, 16),
    bank_info: '',
    notes: '',
    company_id: invoice ? '' : (accounts[0]?.company_id || ''),
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 420, maxWidth: '95vw' }}>
        <h3 style={{ marginTop: 0 }}>{invoice ? `Платёж по счёту №${invoice.invoice_number}` : 'Платёж без счёта'}</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          {!invoice && (
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#666' }}>Компания</span>
              <select value={form.company_id} onChange={set('company_id')}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }}>
                <option value="">— выберите —</option>
                {accounts.map(a => <option key={a.company_id} value={a.company_id}>{a.company_name}</option>)}
              </select>
            </label>
          )}
          <Inp label="Сумма, ₽" type="number" step="0.01" value={form.amount} onChange={set('amount')} />
          <Inp label="Дата получения" type="datetime-local" value={form.received_at} onChange={set('received_at')} />
          <Inp label="Банк / реквизиты" value={form.bank_info} onChange={set('bank_info')} placeholder="Сбербанк, п/п №123..." />
          <Inp label="Примечание" value={form.notes} onChange={set('notes')} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', border: '1px solid #ddd', borderRadius: 6, background: 'white', cursor: 'pointer' }}>Отмена</button>
          <button onClick={() => onSave(form)} style={{ padding: '8px 18px', border: 'none', borderRadius: 6, background: '#27ae60', color: 'white', cursor: 'pointer' }}>
            Зафиксировать
          </button>
        </div>
      </div>
    </div>
  );
}

function DocModal({ invoice_id, payment_id, onClose, onSave }) {
  const [docType, setDocType] = useState('акт');
  const [file, setFile] = useState(null);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 380 }}>
        <h3 style={{ marginTop: 0 }}>Загрузить документ</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>Тип</span>
            <select value={docType} onChange={e => setDocType(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }}>
              <option value="акт">Акт</option>
              <option value="УПД">УПД</option>
              <option value="иное">Иное</option>
            </select>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>Файл</span>
            <input type="file" onChange={e => setFile(e.target.files[0])} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', border: '1px solid #ddd', borderRadius: 6, background: 'white', cursor: 'pointer' }}>Отмена</button>
          <button onClick={() => file && onSave(file, docType)} disabled={!file}
            style={{ padding: '8px 18px', border: 'none', borderRadius: 6, background: '#2980b9', color: 'white', cursor: 'pointer' }}>
            Загрузить
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

function AdminBilling() {
  const { request, authFetch, fetchBlob, API_BASE_URL, showStatus } = useApi();

  const [tab, setTab] = useState('invoices');
  const [loading, setLoading] = useState(false);

  // data
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tariffs, setTariffs] = useState({
    ai_input_per_1k_tokens_rub: 0.2,
    ai_output_per_1k_tokens_rub: 0.8,
    personal_space_free_mb: 200,
    personal_space_overage_rub_per_gb: 100,
  });

  // invoice tab state
  const [invSearch, setInvSearch] = useState('');
  const [invStatus, setInvStatus] = useState('');
  const [expandedInv, setExpandedInv] = useState(null);
  const [payModal, setPayModal] = useState(null);   // invoice | null
  const [standaloneModal, setStandaloneModal] = useState(false);
  const [docModal, setDocModal] = useState(null);   // { invoice_id, payment_id }
  const [invDocs, setInvDocs] = useState({});       // invoiceId → docs[]

  // tx tab state
  const [txSearch, setTxSearch] = useState('');

  // receipts tab state — реальные поступления, а не все транзакции
  const [receipts, setReceipts] = useState(null);
  const [receiptsRange, setReceiptsRange] = useState({ date_from: '', date_to: '' });
  const [receiptsBusy, setReceiptsBusy] = useState(false);

  // balance tab state
  const [adjustForm, setAdjustForm] = useState({ company_id: '', amount: '', description: '' });
  const [adjustBusy, setAdjustBusy] = useState(false);

  // tariff tab state
  const [tariffBusy, setTariffBusy] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const templateInputRef = useRef(null);

  const loadReceipts = useCallback(async () => {
    setReceiptsBusy(true);
    try {
      const qs = new URLSearchParams();
      if (receiptsRange.date_from) qs.set('date_from', receiptsRange.date_from);
      if (receiptsRange.date_to) qs.set('date_to', receiptsRange.date_to);
      const data = await request(`/api/v1/admin/billing/receipts?${qs}`);
      setReceipts(data);
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка загрузки поступлений');
    } finally {
      setReceiptsBusy(false);
    }
  }, [receiptsRange.date_from, receiptsRange.date_to]); // eslint-disable-line

  useEffect(() => {
    if (tab === 'receipts' && !receipts) loadReceipts();
  }, [tab]); // eslint-disable-line

  const selectedAccount = useMemo(
    () => accounts.find(a => a.company_id === adjustForm.company_id) || null,
    [accounts, adjustForm.company_id],
  );

  // ── load ──────────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (invSearch) qs.set('q', invSearch);
      if (invStatus) qs.set('status', invStatus);

      const [invData, txData, accData, settingsData] = await Promise.all([
        request(`/api/v1/admin/billing/invoices?${qs}`),
        request('/api/v1/admin/billing/transactions?limit=200'),
        request('/api/v1/admin/billing/accounts'),
        request('/api/v1/admin/settings'),
      ]);

      setInvoices(invData?.items || []);
      setTransactions(txData?.items || txData || []);
      setAccounts(accData || []);
      if (!adjustForm.company_id && accData?.length) {
        setAdjustForm(f => ({ ...f, company_id: accData[0].company_id }));
      }
      if (settingsData?.billing_tariffs) {
        setTariffs(t => ({ ...t, ...settingsData.billing_tariffs }));
      }
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [invSearch, invStatus]); // eslint-disable-line

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  // ── invoice actions ────────────────────────────────────────────────────────

  const handleDownloadInvoice = async (inv) => {
    try {
      const blob = await fetchBlob(`/api/v1/billing/invoices/${inv.id}/download`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      logger.error('Download failed:', e);
      showStatus('error', 'Ошибка скачивания счёта');
    }
  };

  const handleRecordPayment = async (form) => {
    try {
      if (payModal?.id) {
        await request(`/api/v1/admin/billing/invoices/${payModal.id}/payments`, {
          method: 'POST',
          body: JSON.stringify({
            amount: Number(form.amount),
            received_at: form.received_at,
            bank_info: form.bank_info || null,
            notes: form.notes || null,
          }),
        });
      } else {
        await request('/api/v1/admin/billing/payments', {
          method: 'POST',
          body: JSON.stringify({
            company_id: form.company_id,
            amount: Number(form.amount),
            received_at: form.received_at,
            bank_info: form.bank_info || null,
            notes: form.notes || null,
          }),
        });
      }
      showStatus('success', 'Платёж зафиксирован, баланс пополнен');
      setPayModal(null);
      setStandaloneModal(false);
      await loadAll();
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка при фиксации платежа');
    }
  };

  const handleRollback = async (paymentId) => {
    if (!window.confirm('Откатить платёж? Баланс будет уменьшен.')) return;
    try {
      await request(`/api/v1/admin/billing/invoice-payments/${paymentId}/rollback`, { method: 'POST' });
      showStatus('success', 'Платёж откачен');
      await loadAll();
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка отката');
    }
  };

  const handleUploadDoc = async (file, docType) => {
    const fd = new FormData();
    fd.append('file', file);
    const qs = new URLSearchParams({ doc_type: docType });
    if (docModal.invoice_id) qs.set('invoice_id', docModal.invoice_id);
    if (docModal.payment_id) qs.set('invoice_payment_id', docModal.payment_id);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/admin/billing/closing-docs?${qs}`, { method: 'POST', body: fd });
      showStatus('success', 'Документ загружен');
      setDocModal(null);
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка загрузки документа');
    }
  };

  const loadInvoiceDocs = async (invoiceId) => {
    try {
      const data = await request(`/api/v1/admin/billing/closing-docs?invoice_id=${invoiceId}`);
      setInvDocs(d => ({ ...d, [invoiceId]: data || [] }));
    } catch (e) {
      logger.error(e);
    }
  };

  const toggleExpand = (inv) => {
    if (expandedInv === inv.id) {
      setExpandedInv(null);
    } else {
      setExpandedInv(inv.id);
      loadInvoiceDocs(inv.id);
    }
  };

  // ── tx actions ────────────────────────────────────────────────────────────

  const handleRefundRequest = async (txId) => {
    if (!window.confirm('Создать заявку на возврат?')) return;
    try {
      await request(`/api/v1/admin/billing/transactions/${txId}/refund-request`, {
        method: 'POST', body: JSON.stringify({}),
      });
      showStatus('success', 'Заявка на возврат создана');
      await loadAll();
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка');
    }
  };

  // ── balance actions ───────────────────────────────────────────────────────

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!adjustForm.company_id || !adjustForm.amount) return showStatus('error', 'Укажите плательщика и сумму');
    setAdjustBusy(true);
    try {
      await request('/api/v1/admin/billing/adjustment', {
        method: 'POST',
        body: JSON.stringify({ company_id: adjustForm.company_id, amount: Number(adjustForm.amount), description: adjustForm.description || null }),
      });
      showStatus('success', 'Баланс обновлён');
      setAdjustForm(f => ({ ...f, amount: '', description: '' }));
      await loadAll();
    } catch (e) {
      logger.error(e);
      showStatus('error', 'Ошибка');
    } finally { setAdjustBusy(false); }
  };

  // ── tariff actions ────────────────────────────────────────────────────────

  const handleSaveTariffs = async () => {
    setTariffBusy(true);
    try {
      await request('/api/v1/admin/settings', {
        method: 'POST',
        body: JSON.stringify({ billing_tariffs: Object.fromEntries(Object.entries(tariffs).map(([k, v]) => [k, Number(v)])) }),
      });
      showStatus('success', 'Тарификация сохранена');
    } catch (e) { showStatus('error', 'Ошибка'); } finally { setTariffBusy(false); }
  };

  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setTemplateBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await authFetch(`${API_BASE_URL}/api/v1/admin/billing/invoice-template`, { method: 'POST', body: fd });
      showStatus('success', 'Шаблон загружен');
    } catch (e) { showStatus('error', 'Ошибка загрузки шаблона'); } finally { setTemplateBusy(false); }
  };

  // ── filtered data ─────────────────────────────────────────────────────────

  const filteredTx = useMemo(() => {
    if (!txSearch) return transactions;
    return filterBySearch(transactions, txSearch);
  }, [transactions, txSearch]);

  // ── render ────────────────────────────────────────────────────────────────

  const TH = ({ children, right }) => (
    <th style={{ padding: '10px 12px', textAlign: right ? 'right' : 'left', fontWeight: 600, fontSize: 12, color: '#666', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
      {children}
    </th>
  );
  const TD = ({ children, right, mono, muted }) => (
    <td style={{ padding: '10px 12px', textAlign: right ? 'right' : 'left', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 11 : 13, color: muted ? '#999' : undefined, borderBottom: '1px solid #f5f5f5' }}>
      {children}
    </td>
  );

  return (
    <div className="admin-page">
      {/* modals */}
      {(payModal || standaloneModal) && (
        <PaymentModal
          invoice={payModal || null}
          accounts={accounts}
          onClose={() => { setPayModal(null); setStandaloneModal(false); }}
          onSave={handleRecordPayment}
        />
      )}
      {docModal && (
        <DocModal
          invoice_id={docModal.invoice_id}
          payment_id={docModal.payment_id}
          onClose={() => setDocModal(null)}
          onSave={handleUploadDoc}
        />
      )}

      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Бизнес и финансы</h1>
        <button onClick={loadAll} style={{ padding: '7px 14px', background: 'white', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>
          {loading ? '...' : 'Обновить'}
        </button>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['invoices', 'Счета'], ['receipts', 'Поступления'], ['transactions', 'Транзакции'], ['balances', 'Балансы'], ['tariffs', 'Тарификация']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={TAB_STYLE(tab === id)}>{label}</button>
        ))}
      </div>

      {/* ── INVOICES TAB ─────────────────────────────────────────────────── */}
      {tab === 'invoices' && (
        <div>
          {/* toolbar */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              placeholder="Поиск по счёту, компании, e-mail..."
              value={invSearch} onChange={e => setInvSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadAll()}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, minWidth: 280 }}
            />
            <select value={invStatus} onChange={e => setInvStatus(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }}>
              <option value="">Все статусы</option>
              <option value="issued">Выставлен</option>
              <option value="partial">Частично</option>
              <option value="paid">Оплачен</option>
              <option value="cancelled">Отменён</option>
            </select>
            <button onClick={loadAll} style={{ padding: '8px 14px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Найти
            </button>
            <button onClick={() => setStandaloneModal(true)} style={{ padding: '8px 14px', background: '#27ae60', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', marginLeft: 'auto' }}>
              + Платёж без счёта
            </button>
          </div>

          <Card style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <TH>Счёт №</TH>
                  <TH>Компания / Плательщик</TH>
                  <TH>Пользователь</TH>
                  <TH>Статус</TH>
                  <TH right>Сумма</TH>
                  <TH right>Получено</TH>
                  <TH>Дата</TH>
                  <TH>Действия</TH>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#999' }}>Нет данных</td></tr>
                )}
                {invoices.map(inv => (
                  <React.Fragment key={inv.id}>
                    <tr style={{ opacity: inv.deleted_at ? 0.5 : 1 }}>
                      <TD mono>
                        {inv.invoice_number}
                        {inv.deleted_at && <span style={{ marginLeft: 6, fontSize: 12, color: '#e74c3c' }}>удалён</span>}
                      </TD>
                      <TD>
                        <div style={{ fontWeight: 500 }}>{inv.company_name}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>{inv.payer_name} · ИНН {inv.payer_inn}</div>
                      </TD>
                      <TD>
                        <div>{inv.user_email || '—'}</div>
                        {inv.user_phone && <div style={{ fontSize: 12, color: '#888' }}>{inv.user_phone}</div>}
                        {inv.user_name && <div style={{ fontSize: 12, color: '#888' }}>{inv.user_name}</div>}
                      </TD>
                      <TD><Badge s={inv.status} /></TD>
                      <TD right><strong>{inv.amount?.toLocaleString()} ₽</strong></TD>
                      <TD right style={{ color: inv.paid_total >= inv.amount ? '#27ae60' : inv.paid_total > 0 ? '#2980b9' : '#999' }}>
                        {inv.paid_total > 0 ? `${inv.paid_total.toLocaleString()} ₽` : '—'}
                      </TD>
                      <TD muted>{fmtDate(inv.created_at)}</TD>
                      <TD>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => handleDownloadInvoice(inv)} title="Скачать PDF"
                            style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #ddd', borderRadius: 5, background: 'white', cursor: 'pointer' }}>
                            PDF
                          </button>
                          <button onClick={() => setPayModal(inv)} title="Зафиксировать платёж"
                            style={{ padding: '4px 10px', fontSize: 12, border: 'none', borderRadius: 5, background: '#e8f8f0', color: '#27ae60', cursor: 'pointer' }}>
                            + Оплата
                          </button>
                          <button onClick={() => setDocModal({ invoice_id: inv.id })} title="Загрузить документ"
                            style={{ padding: '4px 10px', fontSize: 12, border: 'none', borderRadius: 5, background: '#eaf3ff', color: '#2980b9', cursor: 'pointer' }}>
                            + Документ
                          </button>
                          <button onClick={() => toggleExpand(inv)}
                            style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #ddd', borderRadius: 5, background: 'white', cursor: 'pointer' }}>
                            {expandedInv === inv.id ? '▲' : '▼'}
                          </button>
                        </div>
                      </TD>
                    </tr>

                    {/* expanded: payments + docs */}
                    {expandedInv === inv.id && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 16px 16px 32px', background: '#f9f9fb' }}>
                          {/* payments */}
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Платежи по счёту</div>
                            {(inv.payments || []).length === 0
                              ? <div style={{ color: '#999', fontSize: 12 }}>Платежей нет</div>
                              : (inv.payments || []).map(p => (
                                <div key={p.id} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eee', fontSize: 13 }}>
                                  <span style={{ fontWeight: 600, color: p.rolled_back_at ? '#e74c3c' : '#27ae60' }}>
                                    {p.rolled_back_at ? '↩ ' : '+'}{p.amount.toLocaleString()} ₽
                                  </span>
                                  <span style={{ color: '#666' }}>{fmtDate(p.received_at)}</span>
                                  {p.bank_info && <span style={{ color: '#888', fontSize: 12 }}>{p.bank_info}</span>}
                                  {p.notes && <span style={{ color: '#888', fontSize: 12 }}>— {p.notes}</span>}
                                  {p.transaction_id && <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#aaa' }}>{p.transaction_id.slice(0, 8)}</span>}
                                  {!p.rolled_back_at && (
                                    <button onClick={() => handleRollback(p.id)}
                                      style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 12, border: 'none', borderRadius: 4, background: '#fff0f0', color: '#e74c3c', cursor: 'pointer' }}>
                                      Откат
                                    </button>
                                  )}
                                </div>
                              ))
                            }
                          </div>

                          {/* closing docs */}
                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Закрывающие документы</div>
                            {(invDocs[inv.id] || []).length === 0
                              ? <div style={{ color: '#999', fontSize: 12 }}>Документов нет</div>
                              : (invDocs[inv.id] || []).map(d => (
                                <div key={d.id} style={{ fontSize: 12, display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ background: '#f0f0f0', borderRadius: 4, padding: '2px 6px' }}>{d.doc_type}</span>
                                  <a href={`${API_BASE_URL}/api/v1/admin/billing/closing-docs/${d.id}/download`} target="_blank" rel="noopener noreferrer"
                                    style={{ color: '#2980b9' }}>
                                    {d.file_name}
                                  </a>
                                  <span style={{ color: '#aaa' }}>{fmtDate(d.created_at)}</span>
                                </div>
                              ))
                            }
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ── TRANSACTIONS TAB ─────────────────────────────────────────────── */}
      {tab === 'receipts' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#666' }}>С даты</span>
              <input type="date" value={receiptsRange.date_from}
                onChange={e => setReceiptsRange(r => ({ ...r, date_from: e.target.value }))}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            </label>
            <label style={{ display: 'grid', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#666' }}>По дату</span>
              <input type="date" value={receiptsRange.date_to}
                onChange={e => setReceiptsRange(r => ({ ...r, date_to: e.target.value }))}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }} />
            </label>
            <button onClick={loadReceipts} disabled={receiptsBusy}
              style={{ padding: '9px 18px', border: 'none', borderRadius: 6, background: '#2c3e50', color: 'white', cursor: 'pointer' }}>
              {receiptsBusy ? '...' : 'Показать'}
            </button>
            <span style={{ fontSize: 12, color: '#999' }}>
              Только фактически пришедшие деньги. Списания, начисления ИИ и корректировки — во вкладке «Транзакции».
            </span>
          </div>

          {receipts && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
                <Card>
                  <div style={{ fontSize: 12, color: '#999' }}>Поступило</div>
                  <div style={{ fontSize: 26, fontWeight: 'bold', color: '#27ae60' }}>
                    {receipts.summary.received.toLocaleString('ru-RU')} ₽
                  </div>
                </Card>
                <Card>
                  <div style={{ fontSize: 12, color: '#999' }}>Возвращено</div>
                  <div style={{ fontSize: 26, fontWeight: 'bold', color: receipts.summary.refunded > 0 ? '#e74c3c' : undefined }}>
                    {receipts.summary.refunded.toLocaleString('ru-RU')} ₽
                  </div>
                </Card>
                <Card>
                  <div style={{ fontSize: 12, color: '#999' }}>Чистыми за {receipts.date_from} — {receipts.date_to}</div>
                  <div style={{ fontSize: 26, fontWeight: 'bold' }}>
                    {receipts.summary.net.toLocaleString('ru-RU')} ₽
                  </div>
                </Card>
              </div>

              {receipts.summary.by_source.length > 0 && (
                <Card style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>Откуда пришло</div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {receipts.summary.by_source.map(src => (
                      <div key={src.source}>
                        <div style={{ fontSize: 13 }}>{SOURCE_LABELS[src.source] || src.source}</div>
                        <div style={{ fontWeight: 'bold' }}>{src.amount.toLocaleString('ru-RU')} ₽</div>
                        <div style={{ fontSize: 12, color: '#aaa' }}>{src.count} платеж.</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {receipts.truncated && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff8e1', borderRadius: 6, fontSize: 13, color: '#8a6d3b' }}>
                  Показаны не все платежи за период — итоги выше посчитаны по всему интервалу, список обрезан.
                </div>
              )}

              <Card style={{ padding: 0, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <TH>Дата</TH>
                      <TH>Плательщик</TH>
                      <TH>Источник</TH>
                      <TH right>Сумма</TH>
                      <TH>Счёт</TH>
                      <TH>Описание</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.items.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#999' }}>За период поступлений нет</td></tr>
                    )}
                    {receipts.items.map(r => (
                      <tr key={r.id}>
                        <TD muted>{fmtDateTime(r.created_at)}</TD>
                        <TD>
                          <div>{r.payer_name}</div>
                          <div style={{ fontSize: 12, color: '#aaa' }}>
                            {r.owner_email || '—'}{r.payer_inn ? ` · ИНН ${r.payer_inn}` : ''}
                          </div>
                          <div style={{ fontSize: 12, color: '#ccc', fontFamily: 'monospace' }}>{r.company_id}</div>
                        </TD>
                        <TD>{SOURCE_LABELS[r.source] || r.source || '—'}</TD>
                        <TD right>
                          <strong style={{ color: r.kind === 'refund' ? '#e74c3c' : '#27ae60' }}>
                            {r.kind === 'refund' ? '-' : '+'}{r.amount.toLocaleString('ru-RU')} ₽
                          </strong>
                        </TD>
                        <TD muted>{r.invoice_number || '—'}</TD>
                        <TD muted>{r.description || '—'}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </div>
      )}

      {tab === 'transactions' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
            <input placeholder="Поиск по ID, компании, e-mail, описанию..."
              value={txSearch} onChange={e => setTxSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, minWidth: 320 }}
            />
            <span style={{ color: '#999', fontSize: 13 }}>{filteredTx.length} записей</span>
          </div>

          <Card style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <TH>ID транзакции</TH>
                  <TH>Компания</TH>
                  <TH>Пользователь</TH>
                  <TH>Тип</TH>
                  <TH right>Сумма</TH>
                  <TH>Статус</TH>
                  <TH>Дата</TH>
                  <TH>Описание</TH>
                  <TH></TH>
                </tr>
              </thead>
              <tbody>
                {filteredTx.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#999' }}>Нет данных</td></tr>
                )}
                {filteredTx.map(tx => (
                  <tr key={tx.id}>
                    <TD mono>
                      <span title={tx.id}>{tx.id?.slice(0, 8)}…</span>
                    </TD>
                    <TD>{tx.company_name}</TD>
                    <TD muted>{tx.user_email || '—'}</TD>
                    <TD><Badge s={tx.transaction_type} /></TD>
                    <TD right><strong style={{ color: tx.amount < 0 ? '#e74c3c' : undefined }}>{tx.amount?.toLocaleString()} ₽</strong></TD>
                    <TD>
                      <div><Badge s={tx.status} /></div>
                      {tx.refund_status && <div style={{ marginTop: 2 }}><Badge s={tx.refund_status} /></div>}
                    </TD>
                    <TD muted>{fmtDateTime(tx.created_at)}</TD>
                    <TD muted>{tx.description}</TD>
                    <TD>
                      {!tx.refund_status && tx.transaction_type !== 'refund' && (
                        <button onClick={() => handleRefundRequest(tx.id)}
                          style={{ padding: '4px 10px', fontSize: 12, border: 'none', borderRadius: 5, background: '#fff0f0', color: '#e74c3c', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          Возврат↗
                        </button>
                      )}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ── BALANCES TAB ─────────────────────────────────────────────────── */}
      {tab === 'balances' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 24 }}>
          <Card>
            <h3 style={{ marginTop: 0 }}>Ручная корректировка</h3>
            <form onSubmit={handleAdjust} style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 12, color: '#666' }}>Плательщик</span>
                <select value={adjustForm.company_id} onChange={e => setAdjustForm(f => ({ ...f, company_id: e.target.value }))}
                  style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }}>
                  <option value="">— выберите —</option>
                  {accounts.map(a => <option key={a.company_id} value={a.company_id}>
                    {a.company_name}{a.owner_email ? ` — ${a.owner_email}` : ''}
                  </option>)}
                </select>
              </label>
              {selectedAccount && (
                <div style={{ padding: '8px 12px', background: '#f8f9fa', borderRadius: 8, fontSize: 13 }}>
                  Баланс: <strong>{selectedAccount.balance?.toLocaleString()} {selectedAccount.currency}</strong>
                </div>
              )}
              <Inp label="Сумма (+ или −)" type="number" step="0.01" value={adjustForm.amount}
                onChange={e => setAdjustForm(f => ({ ...f, amount: e.target.value }))} placeholder="1000 или -500" />
              <Inp label="Комментарий" value={adjustForm.description}
                onChange={e => setAdjustForm(f => ({ ...f, description: e.target.value }))} />
              <button type="submit" disabled={adjustBusy}
                style={{ padding: '9px 18px', border: 'none', borderRadius: 6, background: '#2c3e50', color: 'white', cursor: 'pointer' }}>
                {adjustBusy ? '...' : 'Применить'}
              </button>
            </form>
          </Card>

          <Card style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <TH>Плательщик</TH>
                  <TH right>Баланс</TH>
                  <TH right>В сутки</TH>
                  <TH>Промокод</TH>
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.company_id}>
                    <TD>
                      <div>{a.company_name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {a.owner_email || '—'}{a.inn ? ` · ИНН ${a.inn}` : ''}
                      </div>
                      <div style={{ fontSize: 12, color: '#aaa', fontFamily: 'monospace' }}>{a.company_id}</div>
                      {/* Баланс принадлежит плательщику, а кормятся с него все
                          эти кабинеты — иначе непонятно, за что списывается. */}
                      {a.cabinets?.length > 0 && (
                        <div style={{ fontSize: 12, color: '#697386', marginTop: 4 }}>
                          Кабинеты ({a.cabinets.length}):{' '}
                          {a.cabinets.map(c => `${c.account_name} · ${c.marketplace_type} · ${c.plan_type}`).join('; ')}
                        </div>
                      )}
                      {!a.cabinets?.length && (
                        <div style={{ fontSize: 12, color: '#c0392b', marginTop: 4 }}>Кабинетов на этом балансе нет</div>
                      )}
                    </TD>
                    <TD right>
                      <strong style={{ color: a.balance < 0 ? '#e74c3c' : undefined }}>
                        {Number(a.balance).toLocaleString()} {a.currency}
                      </strong>
                      {a.days_left !== null && a.days_left !== undefined && (
                        <div style={{ fontSize: 12, color: '#aaa' }}>хватит на {a.days_left} дн.</div>
                      )}
                    </TD>
                    <TD right muted>{Number(a.daily_charge || 0).toLocaleString()} ₽</TD>
                    <TD muted>{a.active_promo_code || '—'}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ── TARIFICATION TAB ─────────────────────────────────────────────── */}
      {tab === 'tariffs' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <Card>
            <h3 style={{ marginTop: 0 }}>Тарифные ставки</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              {[
                ['ai_input_per_1k_tokens_rub', 'AI вход 1К токенов, ₽'],
                ['ai_output_per_1k_tokens_rub', 'AI выход 1К токенов, ₽'],
                ['personal_space_free_mb', 'Бесплатное хранилище, МБ'],
                ['personal_space_overage_rub_per_gb', 'Превышение хранилища ₽/ГБ'],
              ].map(([k, label]) => (
                <Inp key={k} label={label} type="number" step="0.001" value={tariffs[k]}
                  onChange={e => setTariffs(t => ({ ...t, [k]: e.target.value }))} />
              ))}
              <button onClick={handleSaveTariffs} disabled={tariffBusy}
                style={{ padding: '9px 18px', border: 'none', borderRadius: 6, background: '#2c3e50', color: 'white', cursor: 'pointer' }}>
                {tariffBusy ? '...' : 'Сохранить'}
              </button>
            </div>
          </Card>

          <Card>
            <h3 style={{ marginTop: 0 }}>Шаблон счёта PDF</h3>
            <p style={{ color: '#666', fontSize: 13 }}>
              Загрузите PDF-шаблон. Текущий шаблон хранится в <code>backend/storage/invoice_template.pdf</code>.
            </p>
            <input ref={templateInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleTemplateUpload} />
            <button onClick={() => templateInputRef.current?.click()} disabled={templateBusy}
              style={{ padding: '9px 18px', border: '1px dashed #ccc', borderRadius: 6, background: 'white', cursor: 'pointer' }}>
              {templateBusy ? 'Загрузка...' : 'Загрузить новый шаблон'}
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default AdminBilling;
